import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ACCESS_TOKEN_EXPIRY_THRESHOLD, REFRESH_TOKEN_MAX_AGE } from "@dentally/auth/auth-constants";
import { FORMATS } from "@shared/constants";
import { Epoch } from "@shared/time-utils";
import dayjs, { Dayjs } from "dayjs";
import jwtDecode from "jwt-decode";
import { StatusCodes } from "http-status-codes";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";
import { CacheService } from "@apps/patient-core/src/app/services/cache.service";
import { WindowService } from "@apps/patient-core/src/app/services/window.service";
import { filterDefined } from "@apps/patient-core/src/app/utils/rxjs";
import { isOnUnsupportedPage } from "@apps/patient-core/src/app/utils/unsupported";
import { lastValueFrom, Observable, of, race } from "rxjs";
import { catchError, filter, first, map, share, tap } from "rxjs/operators";
import { AccessLevel } from "@dentally/enums/access-level";
import { logger } from "@dentally/ui/logger";
import { ConciergeAuthHttpService } from "./auth-http.service";

@Injectable({
  providedIn: "root",
})
export class ConciergeAuthService extends BaseAuthService {
  private _jwtCreatedAt: Dayjs = dayjs();
  private _accessTokenObservable: Observable<string | null> | null = null;

  constructor(cacheService: CacheService, windowService: WindowService, private _authHttpService: ConciergeAuthHttpService) {
    super(cacheService, windowService);

    this.onJWTChanged
      .pipe(
        filterDefined(),
        filter((event) => !!event.jwt)
      )
      .subscribe(() => {
        this._jwtCreatedAt = dayjs();
      });
  }

  public handleInvalidToken(reload?: boolean): null {
    const handleReload = () => {
      if (reload && !isOnUnsupportedPage()) {
        this._windowService.open("/");
      }
    };

    if (this.accessLevel === AccessLevel.PATIENT_IN_PRACTICE) {
      this._authHttpService.logout("invalid-token").subscribe((response) => {
        this.setToken(response.accessToken);
        handleReload();
      });

      return null;
    }

    this.delete();

    handleReload();

    return null;
  }

  public get isRestricted(): boolean {
    return false;
  }

  public get jwtExpiryFormatted(): string | null {
    return dayjs(this._jwtCreatedAt).add(REFRESH_TOKEN_MAX_AGE, "milliseconds").format(FORMATS.LOCALE_DATE_TIME);
  }

  public get jwtCreatedAtFormatted(): string | null {
    return this._jwtCreatedAt.format(FORMATS.LOCALE_DATE_TIME);
  }

  // #region Concierge specific methods
  public async init(): Promise<void> {
    await lastValueFrom(this.getAuthToken());
  }

  private _handleForbiddenError(): Observable<{ accessToken: string } | null | void> {
    return this._authHttpService.logout("403").pipe(
      catchError((error: HttpErrorResponse) => {
        if ((error.status as StatusCodes) === StatusCodes.FORBIDDEN) {
          logger.error("Forbidden error getting auth token", {
            status: error.status,
            statusText: error.statusText,
          });

          // Forbidden means that there is refresh token stored in the cookie but is not valid so we need to unpair
          return this._authHttpService.delete().pipe(
            tap(() => {
              this.delete();
              this._cacheService.clearAll();
              this._windowService.open("/");
            })
          );
        }

        return of(null);
      }),
      tap((response) => {
        if (response) {
          this.setToken(response.accessToken);
        }

        this._windowService.open("/");
      })
    );
  }

  public getAuthToken(_force = false): Observable<string | null> {
    if (this._accessToken) {
      const { exp = 0 } = jwtDecode<{ exp: number }>(this._accessToken);

      if (exp > Epoch.seconds.ceil + ACCESS_TOKEN_EXPIRY_THRESHOLD) {
        return of(this._accessToken);
      }
    }

    if (this._accessTokenObservable !== null) {
      return this._accessTokenObservable;
    }

    // Race between the HTTP request and the logged out event. If the logged out event is first, it will cancel the HTTP request and
    // be used to set the access token for the request that called this method
    this._accessTokenObservable = race(
      this._authHttpService.getAccessToken(),
      this._authHttpService.onLoggedOut.pipe(first()) // Because it's a Subject, it won't trigger from a previous event. The first() is used to complete the observable to match a HTTP request
    ).pipe(
      share(), // Prevent multiple subscribers causing multiple requests to be made
      catchError((error: HttpErrorResponse) => {
        if ((error.status as StatusCodes) === StatusCodes.FORBIDDEN) {
          return this._handleForbiddenError();
        }

        if ((error.status as StatusCodes) !== StatusCodes.UNAUTHORIZED) {
          const errorData = {
            status: error.status,
            statusText: error.statusText,
          };

          logger.error("Error getting auth token", errorData);
        }

        return of(null);
      }),
      map((response) => {
        return response?.accessToken ?? "";
      }),
      tap((accessToken) => {
        if (accessToken) {
          this.setToken(accessToken);
        }

        this._accessTokenObservable = null;
      })
    );

    return this._accessTokenObservable;
  }
  // #endregion
}
